/**
 * As três seções que faltavam na ficha do cliente: contato, dados de acesso e
 * cookies.
 *
 * São o que a barra lateral do Painel edita e o que o front novo só mostrava —
 * quando mostrava. O e-mail era lista morta; telefone, endereço, login e cookie
 * não existiam em lugar nenhum.
 *
 * **Alterar troca o id.** A API mata a linha antiga e insere outra, como o Painel
 * faz, e por isso todo formulário fecha relendo a lista em vez de remendar o item
 * na tela.
 */

import { useState } from "react"
import {
  TITULOS_DE_EMAIL, TITULOS_DE_ENDERECO, TITULOS_DE_TELEFONE, UFS,
  usarCookiesDoCliente, usarConfirmarWhatsapp, usarContatoDoCliente,
  usarDadosDeAcesso, usarEnviarDadosDeAcesso, usarExcluirContato,
  usarLimparAcesso, usarSalvarContato, usarSalvarDadosDeAcesso,
  type EmailDoContato, type EnderecoDoContato, type TelefoneDoContato,
} from "@/api/contatoDoCliente"
import { usarSessao } from "@/sessao/Sessao"
import { Aviso, Botao, Campo, Linha, Secao, Vazio, estiloCampo, mensagem } from "../outros/pecas"

/** O rótulo some quando a pessoa não tem a chave — como o resto da casca faz. */
function Se({ chave, children }: { chave: string; children: React.ReactNode }) {
  const { pode } = usarSessao()
  return pode(chave) ? <>{children}</> : null
}

function telefoneBonito(ddd: number, numero: string) {
  const n = numero.length === 9 ? `${numero.slice(0, 5)}-${numero.slice(5)}`
    : numero.length === 8 ? `${numero.slice(0, 4)}-${numero.slice(4)}`
    : numero
  return `(${ddd}) ${n}`
}

function cepBonito(cep: string) {
  return cep.length === 8 ? `${cep.slice(0, 5)}-${cep.slice(5)}` : cep
}

function quando(iso: string | null) {
  if (!iso) return "—"
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("pt-BR")
}

// ===========================================================================

export function ContatoDoCliente({ clienteId, aoAvisar }: {
  clienteId: number
  aoAvisar: (s: string | null) => void
}) {
  return (
    <>
      <SecaoDeContato clienteId={clienteId} aoAvisar={aoAvisar} />
      <Se chave="cliente_dados"><DadosDeAcesso clienteId={clienteId} aoAvisar={aoAvisar} /></Se>
      <Se chave="cliente_acesso"><CookiesETrocas clienteId={clienteId} aoAvisar={aoAvisar} /></Se>
    </>
  )
}

// =========================================================== contato

type Edicao =
  | { tipo: "email"; item: EmailDoContato | null }
  | { tipo: "telefone"; item: TelefoneDoContato | null }
  | { tipo: "endereco"; item: EnderecoDoContato | null }

function SecaoDeContato({ clienteId, aoAvisar }: {
  clienteId: number
  aoAvisar: (s: string | null) => void
}) {
  const { pode } = usarSessao()
  const contato = usarContatoDoCliente(clienteId)
  const [edicao, setEdicao] = useState<Edicao | null>(null)

  const editavel = pode("cliente_dados")
  const d = contato.data

  return (
    <Secao
      titulo="Contato"
      contagem={d && `${d.emails.length} e-mail(s) · ${d.telefones.length} telefone(s) · ${d.enderecos.length} endereço(s)`}
      acao={editavel && (
        <Linha>
          <Botao aoClicar={() => setEdicao({ tipo: "email", item: null })}>+ E-mail</Botao>
          <Botao aoClicar={() => setEdicao({ tipo: "telefone", item: null })}>+ Telefone</Botao>
          <Botao aoClicar={() => setEdicao({ tipo: "endereco", item: null })}>+ Endereço</Botao>
        </Linha>
      )}
    >
      {contato.isPending && <p style={cinza}>Carregando o contato…</p>}
      {contato.error && <p style={erro}>{mensagem(contato.error)}</p>}

      {d && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Grupo titulo="E-mails" vazio="Sem e-mail — este cliente não consegue entrar no site."
                 itens={d.emails}>
            {(e) => (
              <ItemDeContato
                key={e.id}
                rotulo={e.titulo}
                principal={<a href={`mailto:${e.email}`} style={{ color: "var(--acao)" }}>{e.email}</a>}
                marcas={[
                  e.confirmado ? "confirmado" : null,
                  e.autorizaEnvio ? null : "não recebe envio",
                ]}
                editavel={editavel}
                aoEditar={() => setEdicao({ tipo: "email", item: e })}
                aoExcluir={() => setEdicao(null)}
                clienteId={clienteId} alvo="emails" id={e.id} aoAvisar={aoAvisar}
              />
            )}
          </Grupo>

          <Grupo titulo="Telefones" vazio="Sem telefone." itens={d.telefones}>
            {(t) => (
              <ItemDeContato
                key={t.id}
                rotulo={t.titulo}
                principal={telefoneBonito(t.ddd, t.telefone)}
                marcas={[t.whatsapp ? "WhatsApp" : null]}
                extra={!t.whatsapp && editavel
                  ? <ConfirmarWhats clienteId={clienteId} id={t.id} aoAvisar={aoAvisar} />
                  : null}
                editavel={editavel}
                aoEditar={() => setEdicao({ tipo: "telefone", item: t })}
                aoExcluir={() => setEdicao(null)}
                clienteId={clienteId} alvo="telefones" id={t.id} aoAvisar={aoAvisar}
              />
            )}
          </Grupo>

          <Grupo titulo="Endereços" vazio="Sem endereço." itens={d.enderecos}>
            {(en) => (
              <ItemDeContato
                key={en.id}
                rotulo={en.titulo}
                principal={[en.endereco, en.bairro, `${en.cidade} - ${en.estado}`,
                            en.pais, cepBonito(en.cep)].filter(Boolean).join(". ")}
                marcas={[]}
                editavel={editavel}
                aoEditar={() => setEdicao({ tipo: "endereco", item: en })}
                aoExcluir={() => setEdicao(null)}
                clienteId={clienteId} alvo="enderecos" id={en.id} aoAvisar={aoAvisar}
              />
            )}
          </Grupo>
        </div>
      )}

      {edicao && (
        <FormularioDeContato
          clienteId={clienteId}
          edicao={edicao}
          aoFechar={() => setEdicao(null)}
          aoAvisar={aoAvisar}
        />
      )}
    </Secao>
  )
}

function Grupo<T>({ titulo, itens, vazio, children }: {
  titulo: string
  itens: T[]
  vazio: string
  children: (item: T) => React.ReactNode
}) {
  return (
    <div>
      <h4 style={{ margin: "0 0 5px", fontSize: 10.5, textTransform: "uppercase",
                   letterSpacing: 0.5, color: "var(--txt3)" }}>{titulo}</h4>
      {itens.length === 0
        ? <Vazio>{vazio}</Vazio>
        : <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>{itens.map(children)}</div>}
    </div>
  )
}

function ItemDeContato({
  rotulo, principal, marcas, extra, editavel, aoEditar, aoExcluir,
  clienteId, alvo, id, aoAvisar,
}: {
  rotulo: string
  principal: React.ReactNode
  marcas: (string | null)[]
  extra?: React.ReactNode
  editavel: boolean
  aoEditar: () => void
  aoExcluir: () => void
  clienteId: number
  alvo: "emails" | "telefones" | "enderecos"
  id: number
  aoAvisar: (s: string | null) => void
}) {
  const excluir = usarExcluirContato(clienteId, alvo)
  const [confirmando, setConfirmando] = useState(false)
  const [motivo, setMotivo] = useState("")

  return (
    <div style={{
      display: "flex", alignItems: "baseline", gap: 9, flexWrap: "wrap",
      padding: "5px 7px", borderRadius: 5, background: "var(--surface2)", fontSize: 12,
    }}>
      <span style={{ minWidth: 86, fontSize: 10.5, color: "var(--txt3)" }}>{rotulo}</span>
      <span style={{ flex: 1, minWidth: 180 }}>{principal}</span>
      {marcas.filter(Boolean).map((m) => (
        <span key={m} style={{
          fontSize: 10, padding: "1px 6px", borderRadius: 9,
          background: "var(--surface)", color: "var(--txt2)",
        }}>{m}</span>
      ))}
      {extra}
      {editavel && !confirmando && (
        <Linha>
          <Botao aoClicar={aoEditar}>Alterar</Botao>
          <Botao aoClicar={() => setConfirmando(true)}>Excluir</Botao>
        </Linha>
      )}
      {confirmando && (
        <Linha>
          <input
            style={{ ...estiloCampo, width: 200 }}
            placeholder="Motivo da exclusão"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            autoFocus
          />
          <Botao
            destaque
            desligado={!motivo.trim() || excluir.isPending}
            ocupado={excluir.isPending}
            aoClicar={async () => {
              try {
                await excluir.mutateAsync({ id, motivo })
                aoAvisar("Excluído.")
                setConfirmando(false); setMotivo(""); aoExcluir()
              } catch (e) { aoAvisar(mensagem(e)) }
            }}
          >
            Confirmar
          </Botao>
          <Botao aoClicar={() => { setConfirmando(false); setMotivo("") }}>Cancelar</Botao>
        </Linha>
      )}
    </div>
  )
}

function ConfirmarWhats({ clienteId, id, aoAvisar }: {
  clienteId: number; id: number; aoAvisar: (s: string | null) => void
}) {
  const confirmar = usarConfirmarWhatsapp(clienteId)
  return (
    <Botao
      ocupado={confirmar.isPending}
      titulo="Marcar que este número tem WhatsApp"
      aoClicar={async () => {
        try { await confirmar.mutateAsync(id); aoAvisar("WhatsApp confirmado.") }
        catch (e) { aoAvisar(mensagem(e)) }
      }}
    >
      Confirmar WhatsApp
    </Botao>
  )
}

// ---------------------------------------------------------- o formulário

function FormularioDeContato({ clienteId, edicao, aoFechar, aoAvisar }: {
  clienteId: number
  edicao: Edicao
  aoFechar: () => void
  aoAvisar: (s: string | null) => void
}) {
  const alvo = edicao.tipo === "email" ? "emails"
    : edicao.tipo === "telefone" ? "telefones" : "enderecos"
  const salvar = usarSalvarContato(clienteId, alvo)

  const item = edicao.item as Record<string, unknown> | null
  // O TypeScript une os tres formatos e cada chave ausente vira `undefined`, que
  // nao cabe num Record<string, string>. A anotacao aqui e o que mantem a uniao.
  const [campos, setCampos] = useState<Record<string, string>>((): Record<string, string> => {
    if (edicao.tipo === "email") {
      const e = edicao.item
      return { titulo: e?.titulo ?? "Principal", email: e?.email ?? "",
               autorizaEnvio: String(e?.autorizaEnvio ?? true) }
    }
    if (edicao.tipo === "telefone") {
      const t = edicao.item
      return { titulo: t?.titulo ?? "Principal", ddd: t ? String(t.ddd) : "",
               telefone: t?.telefone ?? "", whatsapp: String(t?.whatsapp ?? false) }
    }
    const en = edicao.item
    return {
      titulo: en?.titulo ?? "Principal", cep: en?.cep ?? "", endereco: en?.endereco ?? "",
      bairro: en?.bairro ?? "", cidade: en?.cidade ?? "", estado: en?.estado ?? "",
      pais: en?.pais ?? "Brasil", codIbge: en ? String(en.codIbge) : "0",
    }
  })
  const [motivo, setMotivo] = useState("")

  const põe = (chave: string) => (e: { target: { value: string } }) =>
    setCampos((c) => ({ ...c, [chave]: e.target.value }))

  const titulos = edicao.tipo === "email" ? TITULOS_DE_EMAIL
    : edicao.tipo === "telefone" ? TITULOS_DE_TELEFONE : TITULOS_DE_ENDERECO

  async function gravar() {
    const dados: Record<string, unknown> =
      edicao.tipo === "email"
        ? { titulo: campos.titulo, email: campos.email,
            autorizaEnvio: campos.autorizaEnvio === "true", motivo }
        : edicao.tipo === "telefone"
        ? { titulo: campos.titulo, ddd: Number(campos.ddd), telefone: campos.telefone,
            whatsapp: campos.whatsapp === "true", motivo }
        : { titulo: campos.titulo, cep: campos.cep, endereco: campos.endereco,
            bairro: campos.bairro, cidade: campos.cidade, estado: campos.estado,
            pais: campos.pais, codIbge: Number(campos.codIbge) || 0, motivo }

    try {
      await salvar.mutateAsync({ id: item ? (item.id as number) : undefined, dados })
      aoAvisar(item ? "Alterado." : "Incluído.")
      aoFechar()
    } catch (e) { aoAvisar(mensagem(e)) }
  }

  return (
    <div style={{
      marginTop: 10, padding: 11, borderRadius: 7,
      border: "1px solid var(--line2)", background: "var(--surface)",
    }}>
      <b style={{ fontSize: 12 }}>
        {item ? "Alterar" : "Incluir"}{" "}
        {edicao.tipo === "email" ? "e-mail" : edicao.tipo === "telefone" ? "telefone" : "endereço"}
      </b>

      {item && (
        <Aviso>
          Alterar aqui <b>não edita a linha</b>: o sistema marca a atual como encerrada e grava
          uma nova, como o Painel de hoje faz. O histórico de contato fica.
        </Aviso>
      )}

      <Linha>
        <Campo rotulo="Título" ajuda="A lista é aberta: há títulos escritos por automações.">
          <input list={`titulos-${edicao.tipo}`} style={estiloCampo}
                 value={campos.titulo} onChange={põe("titulo")} />
          <datalist id={`titulos-${edicao.tipo}`}>
            {titulos.map((t) => <option key={t} value={t} />)}
          </datalist>
        </Campo>

        {edicao.tipo === "email" && (
          <>
            <Campo rotulo="E-mail">
              <input type="email" style={estiloCampo} value={campos.email}
                     onChange={põe("email")} autoFocus />
            </Campo>
            <Campo rotulo="Autoriza envio">
              <select style={estiloCampo} value={campos.autorizaEnvio} onChange={põe("autorizaEnvio")}>
                <option value="true">Sim</option>
                <option value="false">Não</option>
              </select>
            </Campo>
          </>
        )}

        {edicao.tipo === "telefone" && (
          <>
            <Campo rotulo="DDD">
              <input inputMode="numeric" style={{ ...estiloCampo, width: 70 }}
                     value={campos.ddd} onChange={põe("ddd")} autoFocus />
            </Campo>
            <Campo rotulo="Telefone" ajuda="Pode colar com máscara; o DDD não duplica.">
              <input inputMode="tel" style={estiloCampo} value={campos.telefone}
                     onChange={põe("telefone")} />
            </Campo>
            <Campo rotulo="WhatsApp">
              <select style={estiloCampo} value={campos.whatsapp} onChange={põe("whatsapp")}>
                <option value="false">Não confirmado</option>
                <option value="true">Confirmado</option>
              </select>
            </Campo>
          </>
        )}

        {edicao.tipo === "endereco" && (
          <>
            <Campo rotulo="CEP">
              <input inputMode="numeric" style={{ ...estiloCampo, width: 110 }}
                     value={campos.cep} onChange={põe("cep")} autoFocus />
            </Campo>
            <Campo rotulo="Endereço">
              <input style={estiloCampo} value={campos.endereco} onChange={põe("endereco")} />
            </Campo>
            <Campo rotulo="Bairro">
              <input style={estiloCampo} value={campos.bairro} onChange={põe("bairro")} />
            </Campo>
            <Campo rotulo="Cidade">
              <input style={estiloCampo} value={campos.cidade} onChange={põe("cidade")} />
            </Campo>
            <Campo rotulo="Estado">
              <input list="ufs" style={{ ...estiloCampo, width: 110 }}
                     value={campos.estado} onChange={põe("estado")} />
              <datalist id="ufs">{UFS.map((u) => <option key={u} value={u} />)}</datalist>
            </Campo>
            <Campo rotulo="País">
              <input style={estiloCampo} value={campos.pais} onChange={põe("pais")} />
            </Campo>
          </>
        )}
      </Linha>

      <Campo rotulo="Motivo" ajuda="Fica no histórico do cliente, junto com o seu nome.">
        <input style={estiloCampo} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
      </Campo>

      <Linha>
        <Botao destaque desligado={!motivo.trim() || salvar.isPending}
               ocupado={salvar.isPending} aoClicar={gravar}>
          {item ? "Gravar" : "Incluir"}
        </Botao>
        <Botao aoClicar={aoFechar}>Cancelar</Botao>
      </Linha>
    </div>
  )
}

// =================================================== dados de acesso

function DadosDeAcesso({ clienteId, aoAvisar }: {
  clienteId: number
  aoAvisar: (s: string | null) => void
}) {
  const [aberto, setAberto] = useState(false)
  const dados = usarDadosDeAcesso(clienteId, aberto)
  const salvar = usarSalvarDadosDeAcesso(clienteId)
  const enviar = usarEnviarDadosDeAcesso(clienteId)

  const [editando, setEditando] = useState(false)
  const [mostrarSenha, setMostrarSenha] = useState(false)
  const [login, setLogin] = useState("")
  const [senha, setSenha] = useState("")
  const [motivo, setMotivo] = useState("")

  const d = dados.data

  function abrirEdicao() {
    setLogin(d?.definido ? d.login : (d?.loginSugerido ?? ""))
    setSenha(d?.definido ? d.senha : (d?.senhaSugerida ?? ""))
    setMotivo("")
    setEditando(true)
  }

  return (
    <Secao
      titulo="Dados de acesso"
      contagem={aberto && d ? (d.definido ? "login definido" : "sem login") : undefined}
      acao={<Botao aoClicar={() => setAberto((v) => !v)}>{aberto ? "Ocultar" : "Mostrar"}</Botao>}
    >
      {!aberto && (
        <p style={cinza}>
          Usuário e senha do site — é como entram os clientes antigos, <b>37,9% da base</b>.
          Fica fechado porque mostra credencial.
        </p>
      )}

      {aberto && (
        <>
          {dados.isPending && <p style={cinza}>Carregando…</p>}
          {dados.error && <p style={erro}>{mensagem(dados.error)}</p>}

          {d && !editando && (
            <>
              <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 9 }}>
                <div>
                  <div style={rotulo}>Login</div>
                  <div style={valor}>{d.definido ? d.login : "— não definido —"}</div>
                </div>
                <div>
                  <div style={rotulo}>Senha</div>
                  <div style={valor}>
                    {!d.definido ? "—"
                      : mostrarSenha ? d.senha
                      : "•".repeat(Math.max(d.senha.length, 6))}
                    {d.definido && (
                      <button
                        type="button"
                        onClick={() => setMostrarSenha((v) => !v)}
                        style={{ marginLeft: 8, fontSize: 10.5, color: "var(--acao)",
                                 background: "none", border: "none", cursor: "pointer" }}
                      >
                        {mostrarSenha ? "esconder" : "mostrar"}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <Aviso>
                A senha é guardada e enviada <b>em texto plano</b> — é assim no sistema de hoje, e
                mudar isso exige trocar o login do site do cliente na mesma hora.
              </Aviso>

              <Linha>
                <Botao destaque aoClicar={abrirEdicao}>
                  {d.definido ? "Alterar login e senha" : "Definir login e senha"}
                </Botao>
                <Botao
                  desligado={!d.definido || enviar.isPending}
                  ocupado={enviar.isPending}
                  titulo="Manda login e senha para todos os e-mails do cliente"
                  aoClicar={async () => {
                    try {
                      const r = await enviar.mutateAsync()
                      aoAvisar(r.resultado)
                    } catch (e) { aoAvisar(mensagem(e)) }
                  }}
                >
                  Enviar por e-mail
                </Botao>
              </Linha>
            </>
          )}

          {d && editando && (
            <div style={{ padding: 11, borderRadius: 7, border: "1px solid var(--line2)" }}>
              {!d.definido && (
                <Aviso>
                  Já veio preenchido com a sugestão do sistema de hoje: o e-mail principal como
                  login e os <b>6 primeiros dígitos</b> do CPF/CNPJ como senha. Nada foi gravado.
                </Aviso>
              )}
              <Linha>
                <Campo rotulo="Login" ajuda="Mínimo de 4 caracteres. Não pode ser de outro cliente.">
                  <input style={estiloCampo} value={login}
                         onChange={(e) => setLogin(e.target.value)} autoFocus />
                </Campo>
                <Campo rotulo="Senha" ajuda="De 4 a 20 caracteres.">
                  <input style={estiloCampo} value={senha}
                         onChange={(e) => setSenha(e.target.value)} />
                </Campo>
              </Linha>
              <Campo rotulo="Motivo" ajuda="Fica no histórico. A senha não vai para o registro.">
                <input style={estiloCampo} value={motivo}
                       onChange={(e) => setMotivo(e.target.value)} />
              </Campo>
              <Linha>
                <Botao
                  destaque
                  desligado={!motivo.trim() || salvar.isPending}
                  ocupado={salvar.isPending}
                  aoClicar={async () => {
                    try {
                      await salvar.mutateAsync({ login, senha, motivo })
                      aoAvisar("Dados de acesso gravados.")
                      setEditando(false)
                    } catch (e) { aoAvisar(mensagem(e)) }
                  }}
                >
                  Gravar
                </Botao>
                <Botao aoClicar={() => setEditando(false)}>Cancelar</Botao>
              </Linha>
            </div>
          )}
        </>
      )}
    </Secao>
  )
}

// =================================================== cookies e trocas

function CookiesETrocas({ clienteId, aoAvisar }: {
  clienteId: number
  aoAvisar: (s: string | null) => void
}) {
  const [aberto, setAberto] = useState(false)
  const dados = usarCookiesDoCliente(clienteId, aberto)
  const limpar = usarLimparAcesso(clienteId)
  const [confirmando, setConfirmando] = useState<"trocas" | "pc" | "movel" | null>(null)
  const [motivo, setMotivo] = useState("")

  const d = dados.data
  const doPc = d?.cookies.filter((c) => c.navegador.toLowerCase() !== "movel") ?? []
  const doMovel = d?.cookies.filter((c) => c.navegador.toLowerCase() === "movel") ?? []

  return (
    <Secao
      titulo="Cookies e trocas de máquina"
      contagem={aberto && d ? `${d.trocasDeMaquina} troca(s) · ${d.cookies.length} sessão(ões)` : undefined}
      acao={<Botao aoClicar={() => setAberto((v) => !v)}>{aberto ? "Ocultar" : "Mostrar"}</Botao>}
    >
      {!aberto && (
        <p style={cinza}>
          A sessão do cliente fica presa ao navegador. Limpar aqui é o que destrava quem trocou
          de computador e não consegue mais entrar.
        </p>
      )}

      {aberto && (
        <>
          {dados.isPending && <p style={cinza}>Carregando…</p>}
          {dados.error && <p style={erro}>{mensagem(dados.error)}</p>}

          {d && (
            <>
              <p style={{ ...cinza, marginBottom: 9 }}>
                Trocou de máquina <b>{d.trocasDeMaquina}</b> vez(es).{" "}
                PC: <b>{doPc.length}</b> sessão(ões). Móvel: <b>{doMovel.length}</b>.
              </p>

              <Aviso>
                <b>Limpar as trocas apaga o log de acesso inteiro</b> deste cliente — não só as
                linhas de troca de máquina. É o que o Painel de hoje faz, e por isso os{" "}
                {d.ultimosAcessos.length} acessos abaixo somem junto.
              </Aviso>

              {!confirmando && (
                <Linha>
                  <Botao aoClicar={() => { setConfirmando("trocas"); setMotivo("") }}>
                    Limpar trocas de máquina
                  </Botao>
                  <Botao desligado={doPc.length === 0}
                         aoClicar={() => { setConfirmando("pc"); setMotivo("") }}>
                    Limpar cookies PC
                  </Botao>
                  <Botao desligado={doMovel.length === 0}
                         aoClicar={() => { setConfirmando("movel"); setMotivo("") }}>
                    Limpar cookies Móvel
                  </Botao>
                </Linha>
              )}

              {confirmando && (
                <div style={{ padding: 11, borderRadius: 7, border: "1px solid var(--line2)" }}>
                  <b style={{ fontSize: 12 }}>
                    {confirmando === "trocas"
                      ? `Apagar o log de acesso inteiro (${d.ultimosAcessos.length}+ linha(s))?`
                      : `Derrubar a sessão de ${confirmando === "pc" ? "PC" : "celular"} deste cliente?`}
                  </b>
                  <Campo rotulo="Motivo">
                    <input style={estiloCampo} value={motivo} autoFocus
                           onChange={(e) => setMotivo(e.target.value)} />
                  </Campo>
                  <Linha>
                    <Botao
                      destaque
                      desligado={!motivo.trim() || limpar.isPending}
                      ocupado={limpar.isPending}
                      aoClicar={async () => {
                        try {
                          const r = await limpar.mutateAsync({ o_que: confirmando, motivo })
                          aoAvisar(r.resultado)
                          setConfirmando(null); setMotivo("")
                        } catch (e) { aoAvisar(mensagem(e)) }
                      }}
                    >
                      Confirmar
                    </Botao>
                    <Botao aoClicar={() => { setConfirmando(null); setMotivo("") }}>Cancelar</Botao>
                  </Linha>
                </div>
              )}

              {d.ultimosAcessos.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <h4 style={{ margin: "0 0 5px", fontSize: 10.5, textTransform: "uppercase",
                               letterSpacing: 0.5, color: "var(--txt3)" }}>
                    Últimos acessos
                  </h4>
                  <div style={{ maxHeight: 220, overflow: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
                      <thead>
                        <tr style={{ textAlign: "left", color: "var(--txt3)" }}>
                          <th style={celula}>Quando</th>
                          <th style={celula}>Navegador</th>
                          <th style={celula}>Onde</th>
                          <th style={celula}>IP</th>
                          <th style={celula}>O quê</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.ultimosAcessos.map((a, i) => (
                          <tr key={i} style={{ borderTop: "1px solid var(--line)" }}>
                            <td style={celula}>{quando(a.data)}</td>
                            <td style={celula}>{a.navegador || "—"}</td>
                            <td style={celula}>{a.localizacao || a.dominio || "—"}</td>
                            <td style={{ ...celula, fontVariantNumeric: "tabular-nums" }}>{a.ip || "—"}</td>
                            <td style={celula}>{a.texto || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </Secao>
  )
}

const cinza: React.CSSProperties = { fontSize: 11.5, color: "var(--txt3)", margin: 0, lineHeight: 1.5 }
const erro: React.CSSProperties = { fontSize: 11.5, color: "var(--erro)", margin: 0 }
const rotulo: React.CSSProperties = { fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--txt3)" }
const valor: React.CSSProperties = { fontSize: 13, fontFamily: "ui-monospace, monospace" }
const celula: React.CSSProperties = { padding: "4px 7px", verticalAlign: "top" }
